import React, { useEffect, useMemo, useRef } from 'react'
import * as PDFJS from 'pdfjs-dist'
import { wait } from 'async-toolbox'
import { assert } from '../../../lib/util/assert'
import { useAsyncEffect } from '../../hooks/useAsyncEffect'

PDFJS.GlobalWorkerOptions.workerSrc = '/pdfjs/build/pdf.worker.mjs'

interface BlobPdfPreviewProps {
  blobUrl: string
  fileName: string
}

export function BlobPdfPreview({ blobUrl, fileName }: BlobPdfPreviewProps) {
  const ref = useRef<HTMLCanvasElement>(null)
  const lastRenderedUrl = useRef<string>()
  const uniqueId = useMemo<number>(() => Math.floor(Math.random() * 10_000), [])
  const currentRenderTask = useRef<PDFJS.RenderTask | null>(null)
  const currentLoadingTask = useRef<PDFJS.PDFDocumentLoadingTask | null>(null)

  const { loading, error } = useAsyncEffect(async () => {
    if (lastRenderedUrl.current === blobUrl) { return }
    
    // Cancel any ongoing render task
    if (currentRenderTask.current) {
      await currentRenderTask.current.cancel()
    }
    // Cancel any ongoing loading task
    if (currentLoadingTask.current) {
      await currentLoadingTask.current.destroy()
    }
    
    await wait(100)

    const canvasContext = ref.current?.getContext('2d')
    assert(canvasContext)
    
    // Fetch the blob data
    const response = await fetch(blobUrl)
    const pdfBytes = await response.arrayBuffer()
    
    // Store the loading task reference
    currentLoadingTask.current = PDFJS.getDocument(pdfBytes)
    const pdfDoc = await currentLoadingTask.current.promise

    const page = await pdfDoc.getPage(1)
    const viewport = page.getViewport({ scale: 1.0 })
    
    // Get container width and calculate scale
    const container = ref.current?.parentElement
    assert(container)
    const containerWidth = container.clientWidth
    const scale = Math.min(containerWidth / viewport.width, 1.5) // Cap at 1.5x scale
    
    // Apply the calculated scale
    const scaledViewport = page.getViewport({ scale })
    
    // Set canvas dimensions to match scaled viewport
    const canvas = ref.current
    assert(canvas)
    canvas.width = scaledViewport.width
    canvas.height = scaledViewport.height

    await wait(100)

    // Store the render task reference
    currentRenderTask.current = page.render({
      canvasContext,
      viewport: scaledViewport,
    })
    
    // Wait for render to complete
    await currentRenderTask.current.promise
    lastRenderedUrl.current = blobUrl

    // Cleanup function to cancel any pending operations when component unmounts or url changes
    return () => {
      currentRenderTask.current?.cancel()
      currentLoadingTask.current?.destroy()
    }
  }, [blobUrl, uniqueId])

  return (
    <div className="preview preview__pdf h-100">
      {loading && (
        <div className="d-flex justify-content-center align-items-center h-100">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="d-flex justify-content-center align-items-center h-100 text-danger">
          <i className="material-icons">error</i>
        </div>
      )}
      <canvas
        key={[blobUrl, uniqueId].join('/')}
        id={[fileName, uniqueId].join('/')}
        ref={ref}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </div>
  )
}